<script setup>
  import { usePage, router, Link } from '@inertiajs/vue3';
  const page = usePage()
  import { ref, watch, onMounted, onUnmounted } from 'vue'
  import axios from 'axios';
  import ConanTopMenu from '@/Components/Conan/ConanTopMenu.vue';
  import AuthenticationCard from '@/Components/AuthenticationCard.vue';
  import ThemeToggle from '@/Components/ThemeToggle.vue';
  import NotificationBell from '@/Components/NotificationBell.vue';
  import HeaderAnimation from '@/Components/HeaderAnimation.vue';
  import { useHeaderTheme } from '@/Composables/useHeaderTheme.js';

  // Define the props
  const props = defineProps({
    menuItems: {
      type: Array,
      required: false
    }
  })

  const { headerStyle, gradientLineStyle, animationName, animationIntensity, accentOverride } = useHeaderTheme();

  const searchText = ref('');
  const showUserDropdown = ref(false);
  const showMobileMenu = ref(false);

  // Search related state variables
  const searchResults = ref({
    products: [],
    categories: [],
    articles: []
  });
  const isSearching = ref(false);
  const noResultsFound = ref(false);

  let currentXhrRequest = null;
  const source = axios.CancelToken.source();

  const mobileMenuMode = ref(false);

  const handleClickOutside = (event) => {
    if (showUserDropdown.value &&
        !event.target.closest('#userDropdown') &&
        !event.target.closest('#userProfileIcon')) {
      showUserDropdown.value = false;
    }

    const searchWrapper = document.getElementById('search-result-wrapper');
    const searchField = document.getElementById('global-search-field');
    if (searchWrapper &&
        searchWrapper.style.display === 'block' &&
        !searchWrapper.contains(event.target) &&
        event.target !== searchField) {
      searchWrapper.style.display = 'none';
    }
  };

  const checkMobileMode = () => {
    mobileMenuMode.value = window.innerWidth <= 991;
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    checkMobileMode();
    window.addEventListener('resize', checkMobileMode);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('resize', checkMobileMode);
  });

  const toggleUserDropdown = (event) => {
    // Stop propagation to prevent document click handler from closing it immediately
    event.stopPropagation();
    showUserDropdown.value = !showUserDropdown.value;
  }

  const toggleMobileMenu = () => {
    showMobileMenu.value = !showMobileMenu.value;
  }

  const openDropdown = (id) => {
    if (!mobileMenuMode.value) {
      document.getElementById(`dropdown-${id}`).style.display = 'block';
    }
  }

  const closeDropdown = (id) => {
    if (!mobileMenuMode.value) {
      document.getElementById(`dropdown-${id}`).style.display = 'none';
    }
  }

  const keepDropdownOpen = (id) => {
    document.getElementById(`dropdown-${id}`).style.display = 'block';
  }

  const logout = () => {
    router.post(route('logout'));
  }

  const closeSearchResults = () => {
    document.getElementById('search-result-wrapper').style.display = 'none';
  }

  // Watch for changes to searchText to update search results
  watch(searchText, (value) => {
    if (value.length > 0) {
      document.getElementById('search-result-wrapper').style.display = 'block';
      isSearching.value = true;
      noResultsFound.value = false;

      if (currentXhrRequest) {
        source.cancel('Operation canceled by other request.');
      }

      // Call the API to get search results (AJAX only, never via Inertia)
      currentXhrRequest = axios.get(`/api/v1/search?search=${value}`)
        .then((response) => {
          isSearching.value = false;
          if(response.data.length > 0) {
            searchResults.value = {
              products: response.data,
              categories: [],
              articles: []
            };
            noResultsFound.value = false;
          } else {
            searchResults.value = {
              products: [],
              categories: [],
              articles: []
            };
            noResultsFound.value = true;
          }
        })
        .catch((error) => {
          console.error('Search error:', error);
          isSearching.value = false;
          noResultsFound.value = true;
        });
    } else {
      document.getElementById('search-result-wrapper').style.display = 'none';
      isSearching.value = false;
      noResultsFound.value = false;
    }
  });

  const activeDropdown = ref(null);
  
  // Update dropdown behavior for mobile
  /**
   * Topniveauet i menuen er nu et rigtigt link til hovedkategorien.
   *
   * På desktop skal et klik derfor bare føre derhen. På mobil er der ingen
   * hover, så det samme klik skal folde undermenuen ud i stedet, ellers kan
   * man ikke komme til underkategorierne overhovedet.
   */
  const onTopniveauKlik = (event, id) => {
    if (typeof window === 'undefined' || window.innerWidth > 991) {
      return;
    }

    event.preventDefault();
    toggleMobileDropdown(id);
  };

  const toggleMobileDropdown = (id) => {
    if (window.innerWidth <= 991) {
      if (activeDropdown.value === id) {
        activeDropdown.value = null;
      } else {
        activeDropdown.value = id;
      }
      // Ensure the dropdown menu is visible
      const dropdown = document.getElementById(`dropdown-${id}`);
      if (dropdown) {
        dropdown.style.display = activeDropdown.value === id ? 'block' : 'none';
      }
    }
  }
  

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchText.value.trim().length > 0) {
      router.visit(route('search', { q: searchText.value }));
    }
  };
</script>

<template>
  <div class="header-wrapper" :style="headerStyle" style="position: relative;">
    <HeaderAnimation :animation="animationName" :intensity="animationIntensity" />
    <nav class="navbar navbar-expand-xl navbar-pricecompare">
      <div class="container-fluid">
        <!-- Brand -->
        <Link class="navbar-brand d-flex align-items-center me-0 me-xl-3" :href="route('frontpage')">
          <span class="brand-first">Price</span><span class="font-bold">Compare</span>
        </Link>
        
        <!-- Toggle button for mobile -->
        <button class="navbar-toggler border-0" type="button" @click="toggleMobileMenu" 
                aria-controls="navbarContent" aria-expanded="false" aria-label="Toggle navigation">
          <i class="bi bi-list text-white fs-4"></i>
        </button>
        
        <!-- Mobile search button (visible only on small screens) -->
        <button class="d-xl-none btn btn-link text-white p-0 mx-2" type="button" data-bs-toggle="collapse" 
                data-bs-target="#searchCollapse" aria-expanded="false" aria-controls="searchCollapse">
          <i class="bi bi-search fs-4"></i>
        </button>

        <!-- Navbar content -->
        <div class="collapse navbar-collapse" :class="{ 'show': showMobileMenu }" id="navbarContent">
          <!-- Left nav menu -->
          <ul class="navbar-nav mb-2 mb-xl-0 order-3 order-xl-1">
            <li class="nav-item">
              <Link class="nav-link" :class="{'active': route().current('frontpage')}" 
                    aria-current="page" href="/">Forside</Link>
            </li>
            <li v-for="menuItem in page.props.menuItems" :key="menuItem.id" class="nav-item" 
                :class="{ 'dropdown': menuItem.children.length > 0, 'mobile-dropdown-open': activeDropdown === menuItem.id }">
              <!-- For menu items without children -->
              <Link v-if="menuItem.children.length === 0" class="nav-link" 
                    :href="route('mainCategory', { categorySlug: menuItem.slug })">
                {{ menuItem.name }}
              </Link>
              
              <!--
                Topniveauet var href="#". Det gav 25 til 135 døde links på hver
                eneste side, og hovedkategorierne kunne hverken klikkes af en
                bruger på desktop eller findes af en crawler. Nu er det et
                rigtigt link til kategorisiden. På mobil, hvor menuen skal
                kunne foldes ud, opsnappes klikket stadig.
              -->
              <Link v-else class="nav-link dropdown-toggle"
                  :href="route('mainCategory', { categorySlug: menuItem.slug })"
                  @click="onTopniveauKlik($event, menuItem.id)"
                  @mouseover="openDropdown(menuItem.id)" @mouseleave="closeDropdown(menuItem.id)">
                {{ menuItem.name }}
                <i v-if="mobileMenuMode" class="bi ms-1" :class="activeDropdown === menuItem.id ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </Link>
              
              <!-- Megamenu dropdown -->
              <div v-if="menuItem.children.length > 0" :id="`dropdown-${menuItem.id}`" 
                   :class="['dropdown-menu mega-menu', {'show-mobile': activeDropdown === menuItem.id}]" 
                   @mouseover="keepDropdownOpen(menuItem.id)" @mouseleave="closeDropdown(menuItem.id)">
                
                <!-- Mobile back button -->
                <div class="d-xl-none mobile-dropdown-header">
                  <button type="button" class="btn btn-link p-0" @click="activeDropdown = null">
                    <i class="bi bi-arrow-left me-2"></i> Tilbage
                  </button>
                  <div class="mb-0 megamenu-punkt">{{ menuItem.name }}</div>
                </div>
                
                <!-- Megamenu header with parent category -->
                <div class="mega-menu-header d-none d-xl-flex">
                  <div class="mb-0 megamenu-punkt">{{ menuItem.name }}</div>
                  <Link :href="route('mainCategory', { categorySlug: menuItem.slug })" class="btn btn-outline-secondary btn-sm ms-auto">
                    Se alle
                  </Link>
                </div>
                
                <!-- Megamenu grid container -->
                <div class="mega-menu-container">
                  <div v-for="child in menuItem.children" :key="child.id" class="mega-menu-column">
                    <Link class="mega-menu-category" 
                          :href="route('subCategory', { categorySlug: menuItem.slug, subCategorySlug: child.slug })">
                      {{ child.name }}
                    </Link>
                    <ul class="mega-menu-items">
                      <li v-for="subchild in child.children.slice(0, 5)" :key="subchild.id">
                        <Link class="mega-menu-item" 
                              :href="route('subSubCategory', { categorySlug: menuItem.slug, subCategorySlug: child.slug, subSubCategorySlug: subchild.slug })">
                          {{ subchild.name }}
                        </Link>
                      </li>
                      <li v-if="child.children.length > 5">
                        <Link class="mega-menu-item mega-menu-more" 
                              :href="route('subCategory', { categorySlug: menuItem.slug, subCategorySlug: child.slug })">
                          Se alle ({{ child.children.length }}) <i class="bi bi-arrow-right ms-1"></i>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <!-- Megamenu footer with featured items (optional) -->
                <div class="mega-menu-footer d-none d-xl-flex">
                  <div class="row align-items-center">
                    <div class="col-auto">
                      <span class="badge bg-pricecompare-secondary">Populært</span>
                    </div>
                    <div class="col">
                      <!-- Stod som tre href="#". Peger nu på sider der findes. -->
                      <div class="d-flex gap-3">
                        <Link :href="route('mainCategory', { categorySlug: menuItem.slug })" class="text-decoration-none text-secondary">
                          <small>Alt i {{ menuItem.name }}</small>
                        </Link>
                        <Link :href="route('blackFriday')" class="text-decoration-none text-secondary">
                          <small>Tilbud</small>
                        </Link>
                        <Link :href="route('articles')" class="text-decoration-none text-secondary">
                          <small>Guides</small>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            <li class="nav-item">
              <Link class="nav-link" :class="{'active': route().current('articles') || route().current('articles.show') }" :href="route('articles')">
                Artikler
              </Link>
            </li>
            <!--
                PC-byggeren tog Betas plads.
                Beta var en statisk notits om at sitet er i beta — ingen links,
                ingen funktioner, ingen destination — og den stod paa en af de
                faa pladser der er i hovedmenuen. Den er flyttet til footeren,
                hvor de oevrige oplysningssider staar. Byggeren er til gengaeld
                et sted man skal HEN, og det eneste vi har som konkurrenterne
                ikke har.
            -->
            <li class="nav-item">
              <Link class="nav-link pcbygger-link ms-auto"
                    :class="{ 'active': route().current('pcbygger.*') }"
                    :href="route('pcbygger.index')">
                <i class="bi bi-pc-display me-1"></i>PC-bygger
              </Link>
            </li>
          </ul>
          
          <!-- Center search bar - visible on larger screens -->
          <div class="search-bar-wrapper order-1 order-xl-2 mx-auto my-3 my-xl-0 d-none d-xl-block">
            <form class="d-flex position-relative search-form" role="search" @submit.prevent="handleSearchSubmit">
              <input class="form-control me-2 rounded-pill search-input" type="search" name="q" 
                     placeholder="Søg efter produkter, kategorier eller artikler" aria-label="Search" 
                     id="global-search-field" v-model="searchText" @keydown.enter.prevent="handleSearchSubmit" />
              <!-- Kun et ikon indeni: uden aria-label hedder knappen ingenting
                   for en skaermlaeser, og den staar paa hver eneste side. -->
              <button class="btn btn-sm btn-pricecompare-secondary rounded-circle search-button" type="submit"
                      id="global-search-button" aria-label="Søg">
                <i class="bi bi-search"></i>
              </button>
              
              <!-- Updated search results dropdown -->
              <div id="search-result-wrapper" class="search-results-dropdown">
                <div class="search-results-container">
                  <!-- Loading indicator -->
                  <div v-if="isSearching" class="search-loading text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">Søger...</span>
                    </div>
                    <p class="mt-2 mb-0 text-muted">Søger...</p>
                  </div>
                  
                  <!-- No results message -->
                  <div v-else-if="noResultsFound" class="search-no-results p-3 text-center">
                    <i class="bi bi-search text-muted fs-4"></i>
                    <p class="mb-0 mt-2">Ingen resultater fundet for "{{ searchText }}"</p>
                  </div>
                  
                  <!-- Results by type -->
                  <div v-else class="search-results-content">
                    <!-- Products section -->
                    <div v-if="searchResults.products.length > 0" class="search-section">
                      <div class="search-section-header">
                        <div class="search-section-title">
                          <i class="bi bi-box me-2"></i>Produkter
                        </div>
                      </div>
                      <ul class="search-result-list">
                        <li v-for="product in searchResults.products.slice(0, 5)" :key="product.id" class="search-result-item">
                          <a :href="product.productdetails" class="search-result-link" @click="closeSearchResults">
                            <div class="search-result-image">
                              <img :src="product.image" alt="Product" width="50" height="50">
                            </div>
                            <div class="search-result-info">
                              <div class="search-result-name">{{ product.name }}</div>
                              <div class="search-result-price" v-if="product.price">{{ product.price }} kr.</div>
                            </div>
                          </a>
                        </li>
                        <li v-if="searchResults.products.length > 5" class="search-result-more">
                          <Link :href="route('search', { q: searchText })" class="search-view-all">
                            Se alle produkter ({{ searchResults.products.length }})
                            <i class="bi bi-arrow-right ms-1"></i>
                          </Link>
                        </li>
                      </ul>
                    </div>
                    
                    <!-- Categories section - placeholder for future API implementation -->
                    <div v-if="searchResults.categories.length > 0" class="search-section">
                      <div class="search-section-header">
                        <div class="search-section-title">
                          <i class="bi bi-folder me-2"></i>Kategorier
                        </div>
                      </div>
                      <ul class="search-result-list">
                        <li v-for="category in searchResults.categories" :key="category.id" class="search-result-item">
                          <Link :href="category.url" class="search-result-link" @click="closeSearchResults">
                            <div class="search-result-icon">
                              <i class="bi bi-folder text-primary"></i>
                            </div>
                            <div class="search-result-info">
                              <div class="search-result-name">{{ category.name }}</div>
                            </div>
                          </Link>
                        </li>
                      </ul>
                    </div>
                    
                    <!-- Articles section - placeholder for future API implementation -->
                    <div v-if="searchResults.articles.length > 0" class="search-section">
                      <div class="search-section-header">
                        <div class="search-section-title">
                          <i class="bi bi-file-text me-2"></i>Artikler
                        </div>
                      </div>
                      <ul class="search-result-list">
                        <li v-for="article in searchResults.articles" :key="article.id" class="search-result-item">
                          <Link :href="article.url" class="search-result-link" @click="closeSearchResults">
                            <div class="search-result-icon">
                              <i class="bi bi-file-text text-info"></i>
                            </div>
                            <div class="search-result-info">
                              <div class="search-result-name">{{ article.title }}</div>
                              <div class="search-result-date">{{ article.date }}</div>
                            </div>
                          </Link>
                        </li>
                      </ul>
                    </div>
                    
                  </div>
                </div>
              </div>
            </form>
          </div>
          
          <!-- Right icons -->
          <div class="d-flex align-items-center ms-auto order-2 order-xl-3">
            <!-- Notification icon - only visible for logged in users -->
            <div v-if="page.props.auth.user" class="me-3 d-none d-sm-block">
              <NotificationBell :darkMode="true" />
            </div>
            
            <!-- Theme toggle - only visible for logged in users -->
            <div v-if="page.props.auth.user" class="nav-icon-wrapper me-3 d-none d-sm-block">
              <ThemeToggle />
            </div>
            
            <!-- Admin icon - only visible for logged in pricecompare employees -->
            <div v-if="page.props.auth.user && page.props.auth.user.is_pricecompare_employee" class="nav-icon-wrapper me-3 d-none d-sm-block">
              <Link :href="route('admin.dashboard')" class="text-white position-relative" aria-label="Administration">
                <i class="bi bi-shield-lock-fill fs-5"></i>
                <span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning">
                  <span class="visually-hidden">Admin</span>
                </span>
              </Link>
            </div>
            
            <!-- User profile icon and dropdown -->
            <div class="nav-icon-wrapper position-relative">
              <a href="#" id="userProfileIcon" @click.stop.prevent="toggleUserDropdown($event)"
                 class="text-white d-flex align-items-center"
                 :aria-label="page.props.auth.user ? 'Din konto' : 'Log ind'"
                 aria-haspopup="true" :aria-expanded="showUserDropdown ? 'true' : 'false'">
                <i v-if="!page.props.auth.user" class="bi bi-person-circle fs-4"></i>
                <!-- Reverse the colors for better contrast with the top navigation header -->
                <img v-else :src="`https://ui-avatars.com/api/?name=${page.props.auth.user.name}&color=1E3A8A&background=FF5733`" 
                     class="rounded-circle user-avatar" width="32" height="32" :alt="page.props.auth.user?.name || 'User'" />
              </a>
              
              <!-- Redesigned User dropdown - using v-if for better debugging -->
              <div id="userDropdown" v-if="showUserDropdown" 
                   class="user-dropdown position-absolute end-0 bg-white rounded shadow"
                   @click.stop>
                <!-- User info section when logged in -->
                <div v-if="page.props.auth.user" class="user-dropdown-header p-3 border-bottom">
                  <div class="d-flex align-items-center">
                    <img :src="`https://ui-avatars.com/api/?name=${page.props.auth.user.name}&color=1E3A8A&background=FF5733`" 
                         class="rounded-circle me-3" width="48" height="48" 
                         :alt="page.props.auth.user?.name || 'User'" />
                    <div>
                      <p class="mb-0 fw-bold text-primary">{{ page.props.auth.user.name }}</p>
                      <small class="text-muted">{{ page.props.auth.user.email }}</small>
                    </div>
                  </div>
                </div>
                
                <!-- Welcome section when not logged in -->
                <div v-else class="user-dropdown-header p-3 border-bottom bg-light">
                  <div class="mb-0 fw-bold">Velkommen til PriceCompare!</div>
                  <p class="mb-0 small text-muted">Log ind for at få personlige anbefalinger</p>
                </div>
                
                <!-- Menu items for non-logged in users -->
                <div v-if="!page.props.auth.user" class="p-3">
                  <div class="d-grid gap-3">
                    <Link class="btn btn-primary text-white auth-btn py-2" :href="route('login')">
                      <i class="bi bi-box-arrow-in-right me-2"></i> Log ind
                    </Link>
                    <Link class="btn btn-outline-primary auth-btn py-2" :href="route('register')">
                      <i class="bi bi-person-plus me-2"></i> Opret bruger
                    </Link>
                  </div>
                  <div class="mt-3 pt-3 border-top text-center">
                    <a href="#" class="text-decoration-none text-muted small">
                      <i class="bi bi-question-circle me-1"></i> Hjælp & support
                    </a>
                  </div>
                </div>
                
                <!-- Menu items for logged in users -->
                <div v-else>
                  <Link class="dropdown-item user-dropdown-item" :href="route('dashboard')">
                    <i class="bi bi-person me-2"></i> Min profil
                  </Link>
                  <Link class="dropdown-item user-dropdown-item" :href="route('user.favorites')">
                    <i class="bi bi-heart me-2"></i> Mine favoritter
                  </Link>
                  <Link class="dropdown-item user-dropdown-item" :href="route('pcbygger.mine')">
                    <i class="bi bi-pc-display me-2"></i> Mine builds
                  </Link>
                  <Link class="dropdown-item user-dropdown-item" :href="route('user.settings')">
                    <i class="bi bi-gear me-2"></i> Indstillinger
                  </Link>
                  <div class="dropdown-divider"></div>
                  <a class="dropdown-item user-dropdown-item text-danger" href="#" @click.prevent="logout">
                    <i class="bi bi-box-arrow-right me-2"></i> Log ud
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    
    <!-- Collapsible search for mobile -->
    <div class="collapse navbar-collapse bg-white py-2" id="searchCollapse">
      <div class="container">
        <form class="d-flex position-relative" role="search" @submit.prevent="handleSearchSubmit">
          <input class="form-control me-2 rounded-pill" type="search" name="q" 
                 placeholder="Søg efter produkter..." aria-label="Search" 
                 v-model="searchText" @keydown.enter.prevent="handleSearchSubmit" />
          <button class="btn btn-pricecompare-secondary rounded-circle" type="submit">
            <i class="bi bi-search"></i>
          </button>
          
          <!-- Mobile search results (same structure, different styling) -->
          <div id="search-result-wrapper-mobile" class="search-results-dropdown search-results-mobile" 
               :style="{ display: searchText.length > 0 ? 'block' : 'none' }">
            <div class="search-results-container">
              <!-- Loading indicator -->
              <div v-if="isSearching" class="search-loading text-center py-4">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">Søger...</span>
                </div>
                <p class="mt-2 mb-0 text-muted">Søger...</p>
              </div>
              
              <!-- No results message -->
              <div v-else-if="noResultsFound" class="search-no-results p-3 text-center">
                <i class="bi bi-search text-muted fs-4"></i>
                <p class="mb-0 mt-2">Ingen resultater fundet for "{{ searchText }}"</p>
              </div>
              
              <!-- Results by type -->
              <div v-else class="search-results-content">
                <!-- Products section -->
                <div v-if="searchResults.products.length > 0" class="search-section">
                  <div class="search-section-header">
                    <div class="search-section-title">
                      <i class="bi bi-box me-2"></i>Produkter
                    </div>
                  </div>
                  <ul class="search-result-list">
                    <li v-for="product in searchResults.products.slice(0, 4)" :key="product.id" class="search-result-item">
                      <a :href="product.productdetails" class="search-result-link" @click="closeSearchResults">
                        <div class="search-result-image">
                          <img :src="product.image" alt="Product" width="40" height="40">
                        </div>
                        <div class="search-result-info">
                          <div class="search-result-name">{{ product.name }}</div>
                        </div>
                      </a>
                    </li>
                    <li v-if="searchResults.products.length > 4" class="search-result-more">
                      <Link :href="route('search', { q: searchText })" class="search-view-all">
                        Se alle produkter ({{ searchResults.products.length }})
                        <i class="bi bi-arrow-right ms-1"></i>
                      </Link>
                    </li>
                  </ul>
                </div>
                
                <!-- Categories and Articles would use the same structure -->
                
                <!-- View all results link -->
                <div class="search-footer">
                  <Link :href="route('search', { q: searchText })" class="btn btn-light btn-sm w-100">
                    <i class="bi bi-search me-1"></i> Se alle resultater
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="header-gradient-line" :style="gradientLineStyle"></div>
  </div>
</template>

<style scoped>
/* Sitets accent, ikke Bootstraps advarselsgul: det er en destination, ikke
   en advarsel. */
.pcbygger-link { color: var(--pc-accent) !important; font-weight: 600; }
.pcbygger-link:hover { color: var(--pc-accent-hover) !important; }

  .header-wrapper {
    background-color: var(--pc-bg-secondary);
    box-shadow: var(--pc-shadow);
  }

  .navbar-pricecompare {
    background-color: transparent;
    padding: 0.75rem 1rem;
  }

  .navbar-brand {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--pc-text-primary);
    margin-right: 0;
  }

  .brand-first {
    background: var(--pc-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
  }

  /* Updated menu items styling */
  .nav-link {
    color: var(--pc-text-secondary);
    font-weight: 500;
    transition: all 0.2s ease;
    padding: 0.5rem 1rem;
    border-radius: 0.25rem;
    display: block;
    text-decoration: none;
    position: relative;
  }

  .nav-link:hover, .nav-link:focus {
    color: var(--pc-text-primary);
    background-color: var(--pc-bg-hover);
    transform: translateY(-1px);
  }

  .nav-link.active {
    color: var(--pc-text-primary);
    font-weight: 600;
    background-color: var(--pc-accent-glow);
  }

  .nav-link.active:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 1rem;
    right: 1rem;
    height: 3px;
    background-color: var(--pc-accent);
    border-radius: 3px 3px 0 0;
  }

  .dropdown-toggle {
    display: flex;
    align-items: center;
  }

  .dropdown-toggle:after {
    display: none;
  }

  /* Remove old top-level-menu class styling since we're using consistent nav-links */

  .dropdown-full-width {
    margin: auto !important;
    left: 15px !important;
    right: 15px !important;
    border-radius: 0.5rem;
    box-shadow: var(--pc-shadow-lg);
    border: none;
    padding: 1rem;
    animation: fadeDown 0.2s ease-out;
  }

  @keyframes fadeDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .middle-level-menu {
    font-weight: 600;
    color: var(--pc-accent);
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
  }

  .middle-level-menu:hover {
    background-color: var(--pc-bg-hover);
    color: var(--pc-accent);
  }

  .bottom-level-menu {
    font-size: 0.9rem;
    padding: 0.4rem 0.75rem;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
  }

  .bottom-level-menu:hover {
    background-color: var(--pc-bg-hover);
  }

  /* Megamenu styling */
  .mega-menu {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: var(--pc-shadow-lg) !important;
    animation: fadeDown 0.25s ease-out;
    z-index: 1030 !important;
    max-height: calc(100vh - 75px);
    overflow-y: auto;
    background-color: var(--pc-bg-secondary);
  }

  .mega-menu-header {
    padding: 1rem 1rem 0.5rem;
    border-bottom: 1px solid var(--pc-border);
    margin: 0 1rem;
    display: flex;
    align-items: center;
  }

  .mega-menu-header h5 {
    color: var(--pc-accent);
    font-weight: 600;
    margin: 0;
  }

  .mega-menu-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
    padding: 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
  }

  .mega-menu-column {
    display: flex;
    flex-direction: column;
    min-width: 180px;
  }

  .mega-menu-category {
    font-weight: 600;
    color: var(--pc-accent);
    text-decoration: none;
    padding: 0.5rem;
    border-radius: 0.25rem;
    margin-bottom: 0.5rem;
    transition: all 0.2s ease;
  }

  .mega-menu-category:hover {
    background-color: var(--pc-bg-hover);
  }

  .mega-menu-items {
    list-style: none;
    padding: 0 0 0 0.5rem;
    margin: 0;
  }

  .mega-menu-item {
    color: var(--pc-text-secondary);
    text-decoration: none;
    padding: 0.375rem 0.5rem;
    display: block;
    font-size: 0.9rem;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
  }

  .mega-menu-item:hover {
    background-color: var(--pc-bg-hover);
    color: var(--pc-text-primary);
  }

  .mega-menu-more {
    color: var(--pc-text-muted);
    font-style: italic;
    font-size: 0.85rem;
  }

  .mega-menu-footer {
    border-top: 1px solid var(--pc-border);
    padding: 0.75rem 1.5rem;
    margin-top: 0.5rem;
    background-color: var(--pc-bg-hover);
  }

  /* Make sure dropdown animation still works */
  @keyframes fadeDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Responsive adjustments for megamenu */
  @media (max-width: 1199px) {
    .mega-menu {
      display: none;
      position: fixed !important;
      top: 0 !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      margin: 0 !important;
      border-radius: 0 !important;
      z-index: 1070 !important;
      animation: none;
      max-height: none;
      background-color: var(--pc-bg-secondary);
    }

    .show-mobile {
      display: block !important;
    }

    .mega-menu-container {
      display: block;
      padding: 1rem;
      overflow-y: auto;
      max-height: calc(100vh - 60px);
    }

    .mega-menu-column {
      border-bottom: 1px solid var(--pc-border);
      margin-bottom: 1rem;
      padding-bottom: 1rem;
    }

    .mega-menu-column:last-child {
      border-bottom: none;
      margin-bottom: 0;
    }

    .mega-menu-category {
      font-size: 1rem;
    }

    .mega-menu-items {
      padding-left: 1rem;
    }

    .mega-menu-item {
      padding: 0.5rem 0;
    }
  }

  /* Mobile specific styles */
  @media (max-width: 1199px) {
    .navbar-collapse {
      position: absolute;
      top: 70px;
      left: 0;
      right: 0;
      background: var(--pc-bg-secondary);
      z-index: 1000;
      box-shadow: var(--pc-shadow);
      padding: 1rem;
    }

    .nav-link {
      color: var(--pc-text-primary);
      border-bottom: 1px solid var(--pc-border);
      padding: 0.7rem 0;
      border-radius: 0;
    }

    .nav-link:hover, .nav-link:focus {
      background-color: transparent;
      transform: none;
      color: var(--pc-accent);
    }

    .nav-link.active {
      background-color: transparent;
    }

    .nav-link.active:after {
      left: 0;
      right: 0;
      bottom: -1px;
    }

    .mobile-dropdown-open .nav-link {
      color: var(--pc-accent);
    }
  }

  /* Updated search bar styling */
  .search-bar-wrapper {
    position: relative;
    width: 100%;
    max-width: 450px;
  }

  .search-form {
    width: 100%;
  }

  .search-input {
    height: 40px;
    font-size: 0.95rem;
    padding-right: 40px;
    background: var(--pc-bg-tertiary);
    border: 1px solid var(--pc-border-hover);
    color: var(--pc-text-primary);
  }

  .search-input::placeholder {
    color: var(--pc-text-muted);
  }

  .search-input:focus {
    border-color: var(--pc-accent);
    box-shadow: 0 0 0 3px var(--pc-accent-glow);
  }

  .search-button {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 34px;
    height: 34px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--pc-accent);
    color: var(--pc-bg-primary);
    border-radius: 50%;
    border: none;
  }

  /* Ensure the search-results-dropdown stays under the search input */
  .search-results-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1050;
    background-color: var(--pc-bg-secondary);
    border-radius: 0.5rem;
    box-shadow: var(--pc-shadow-lg);
    margin-top: 5px;
    display: none;
    max-height: 80vh;
    overflow-y: auto;
    border: 1px solid var(--pc-border);
  }

  /*
   * Baandet mellem lg og xl: menuen fylder mere end der er.
   *
   * Maalt paa forsiden ved 992px: selve .navbar-nav er 876px bred i en
   * container paa 960, og saa er der hverken plads til soegefeltet eller
   * ikonerne — 155px vandret rul paa hver eneste sidetype, i begge temaer.
   * Det er Bootstraps lg-graense, altsaa praecis der hvor desktopmenuen
   * folder sig ud, saa det rammer enhver tablet paa tvaers.
   *
   * Mindre polstring og lidt mindre skrift paa menupunkterne henter de 155px
   * uden at fjerne noget fra menuen. Over 1200px er alt som foer.
   */
  /*
   * "PC-bygger" braekkede paa bindestregen.
   *
   * MAALT i browseren 16. oktober 2026 paa produktsiden: menupunktet stod paa
   * to linjer ved HVER bredde fra 992 til 1600px — kun 1920 var ren. Menuen
   * blev 64px hoej i stedet for 40, og den ekstra linje pressede hele baandet.
   * Det er ikke pladsmangel, det er ordombrydning: CSS maa braekke efter en
   * bindestreg, og menupunktet har én.
   *
   * Nowrap alene goer menuen bredere, saa den smallere polstring nedenfor
   * foelger med i baandet hvor der er mindst plads.
   */
  .navbar-nav .nav-link {
    white-space: nowrap;
  }

  /*
   * Baandet mellem xl og 1400: menuen fylder mere end der er.
   *
   * Maalt efter nowrap: ved 1280px blev soegefeltet 221px bredt mod 161 foer,
   * fordi menuen holdt op med at tage to linjer. Over 1400px er alt som foer.
   */
  @media (max-width: 1400px) and (min-width: 1200px) {
    .navbar-nav .nav-link {
      padding-left: 0.35rem;
      padding-right: 0.35rem;
      font-size: 0.86rem;
    }
  }

  /* User dropdown styling - UPDATED */
  .user-dropdown {
    min-width: 280px;
    z-index: 1050;
    top: 100%;
    margin-top: 0.75rem;
    border-radius: 0.5rem;
    overflow: hidden;
    background-color: var(--pc-bg-secondary);
    box-shadow: var(--pc-shadow-lg);
    border: 1px solid var(--pc-border);
    transform-origin: top right;
    animation: dropdownFade 0.2s ease;
  }

  @keyframes dropdownFade {
    from {
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .user-dropdown-header {
    background-color: var(--pc-bg-hover);
    border-bottom: 1px solid var(--pc-border);
  }

  .user-dropdown-header h6 {
    color: var(--pc-accent);
  }

  .user-dropdown-item {
    padding: 0.8rem 1.25rem;
    transition: all 0.2s ease;
    color: var(--pc-text-secondary);
  }

  .user-dropdown-item:hover {
    background-color: var(--pc-bg-hover);
    color: var(--pc-text-primary);
  }

  .auth-btn {
    padding: 0.6rem 1rem;
    font-weight: 500;
    border-radius: 0.375rem;
    transition: all 0.2s ease;
  }

  .btn-primary {
    background-color: var(--pc-accent);
    border-color: var(--pc-accent);
  }

  .btn-primary:hover {
    background-color: var(--pc-accent);
    border-color: var(--pc-accent);
    filter: brightness(0.9);
  }

  .btn-outline-primary {
    color: var(--pc-accent);
    border-color: var(--pc-accent);
  }

  .btn-outline-primary:hover {
    background-color: var(--pc-bg-hover);
    color: var(--pc-accent);
  }

  /* Mobile adjustments for user dropdown */
  @media (max-width: 576px) {
    .user-dropdown {
      position: fixed;
      width: calc(100% - 2rem);
      left: 1rem;
      right: 1rem;
      top: auto;
      bottom: 1rem;
      margin-top: 0;
      box-shadow: var(--pc-shadow-lg);
    }
  }

  /* Add additional style for theme toggle in navbar */
  :deep(.theme-toggle) {
    color: var(--pc-text-primary);
  }

  :deep(.theme-toggle:hover) {
    background-color: var(--pc-accent-glow);
  }

  /* Updated search-result-list styling */
  .search-result-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .search-result-item {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border-bottom: 1px solid var(--pc-border);
    transition: background-color 0.2s ease;
  }

  .search-result-item:last-child {
    border-bottom: none;
  }

  .search-result-item:hover {
    background-color: var(--pc-bg-hover);
  }

  .search-result-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: var(--pc-text-secondary);
    width: 100%;
  }

  .search-result-link:hover {
    color: var(--pc-text-primary);
  }

  .search-result-image img {
    border-radius: 0.25rem;
    object-fit: cover;
    margin-right: 0.75rem;
  }

  .search-result-info {
    flex: 1;
  }

  .search-result-name {
    font-weight: 500;
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  .search-result-price {
    font-size: 0.85rem;
    color: var(--pc-text-muted);
  }

  .search-result-more {
    text-align: center;
    padding: 0.5rem;
    font-style: italic;
    color: var(--pc-text-muted);
  }

  .search-result-more a {
    text-decoration: none;
    color: var(--pc-accent);
  }

  .search-result-more a:hover {
    text-decoration: underline;
  }

  .mobile-dropdown-open .nav-link {
    color: var(--pc-accent);
  }

  .header-gradient-line {
    height: 2px;
    background: var(--pc-gradient-line);
    width: 100%;
  }
</style>